import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronRight, Flag, PhoneCall, CheckCircle2, XCircle, ShieldQuestion, Clock, Link2, Bot, RefreshCw, Brain, Lightbulb, Zap, FileCheck, Send, Bell, MoreVertical, Archive, Trash2, RotateCcw, Download, Ban, ShieldAlert, Car, Hand, FolderX, Mail } from "lucide-react";
import ReminderDialog from "./ReminderDialog";
import ManualReservation from "./ManualReservation";
import QuickConvertDialog from "./QuickConvertDialog";
import WaitlistSyncPanel from "./WaitlistSyncPanel";
import AdminDocUpload, { DocFile } from "./AdminDocUpload";
import RiskBadge from "./RiskBadge";
import { fmtDateTime } from "@/lib/datetime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New Request", "Contact Needed", "Phone Screening Scheduled", "Under Review", "Documents Requested",
  "Approved", "Approved With Conditions", "Declined", "No Response", "Converted to Booking", "Withdrawn",
  "Still Interested", "Not Interested", "Opted Out", "Follow-Up Unanswered",
  "Waiting List", "Vehicle Offered", "Closed – No Response", "No Longer Needed", "Waiting List – No Vehicle Available"];
const STATUS_COLOR = {
  "New Request": "bg-[#F2B705]/15 text-[#F2B705]", "Approved": "bg-green-500/15 text-green-400",
  "Approved With Conditions": "bg-green-500/15 text-green-400", "Declined": "bg-red-500/15 text-red-400",
  "Converted to Booking": "bg-blue-500/15 text-blue-300", "No Response": "bg-orange-500/15 text-orange-300",
  "Still Interested": "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40",
  "Not Interested": "bg-white/5 text-white/40", "Opted Out": "bg-red-500/10 text-red-300",
  "Follow-Up Unanswered": "bg-orange-500/10 text-orange-200",
  "Waiting List": "bg-amber-400/15 text-amber-300 border border-amber-400/40",
  "Waiting List – No Vehicle Available": "bg-amber-400/15 text-amber-300 border border-amber-400/40",
  "Vehicle Offered": "bg-green-500/20 text-green-300 border border-green-500/40",
  "Closed – No Response": "bg-white/5 text-white/40",
  "No Longer Needed": "bg-white/5 text-white/40",
};
const CONDITION_OPTIONS = ["Additional documentation required", "Missouri or Illinois address verification required",
  "Insurance verification required", "Additional payment condition", "Different vehicle required",
  "Shorter rental period", "Local travel only", "No rideshare use", "Additional driver not approved",
  "Management call required before pickup"];
const SCREEN_CHECKS = [["customer_answered", "Customer Answered"], ["identity_confirmed", "Identity Confirmed"],
  ["purpose_confirmed", "Rental Purpose Confirmed"], ["dates_confirmed", "Dates Confirmed"],
  ["driver_info_confirmed", "Driver Info Confirmed"], ["insurance_discussed", "Insurance Discussed"],
  ["additional_drivers_discussed", "Additional Drivers Discussed"], ["return_policy_explained", "Return Policy Explained"],
  ["extension_policy_explained", "Extension Policy Explained"], ["travel_restrictions_explained", "Travel Restrictions Explained"]];
const LEVEL_COLOR = { "Low Review Concern": "text-green-400", "Additional Review Needed": "text-[#F2B705]", "High Review Concern": "text-red-400" };
const AI_EMOJI = { "Recommended for Approval": "🟢", "Needs Additional Review": "🟡", "Missing Documents": "🟠", "Hard Decline": "🔴" };
const AI_COLOR = { "Recommended for Approval": "text-green-400", "Needs Additional Review": "text-[#F2B705]", "Missing Documents": "text-orange-300", "Hard Decline": "text-red-400" };

function AiReviewPanel({ r, H, reload }) {
  const [regen, setRegen] = useState(false);
  const [asking, setAsking] = useState("");
  const [answers, setAnswers] = useState({ explain: r.ai_review?.explain, owner_take: r.ai_review?.owner_take });
  const ai = r.ai_review;
  const regenerate = async () => {
    setRegen(true);
    try { await axios.post(`${API}/reservations/${r.id}/ai-review`, {}, H); toast.success("AI review regenerated."); reload(); }
    catch { toast.error("Could not regenerate AI review."); }
    finally { setRegen(false); }
  };
  const ask = async (mode) => {
    setAsking(mode);
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/ai-ask`, { mode }, H);
      setAnswers((a) => ({ ...a, [mode]: data.text }));
    } catch { toast.error("AI could not answer right now — try again."); }
    finally { setAsking(""); }
  };
  return (
    <div data-testid={`ai-review-panel-${r.id}`} className="bg-black/40 border border-[#F2B705]/25 rounded-md p-3">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <Bot size={14} className="text-[#F2B705]" />
        <span className="text-white font-semibold text-sm">AI Assistant Review</span>
        {ai ? (
          <span className={`text-sm font-bold ${AI_COLOR[ai.recommendation] || "text-white"}`}>
            {AI_EMOJI[ai.recommendation]} {ai.recommendation} · Confidence {ai.confidence}%
          </span>
        ) : <span className="text-white/40 text-xs">No AI review yet.</span>}
        <button data-testid={`ai-regen-btn-${r.id}`} onClick={regenerate} disabled={regen}
          className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-white/60 hover:text-white text-xs disabled:opacity-50">
          {regen ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Regenerate
        </button>
      </div>
      {ai && (
        <>
          {ai.summary && <p data-testid={`ai-summary-${r.id}`} className="text-white/80 text-sm italic border-l-2 border-[#F2B705]/50 pl-2.5 mb-2">"{ai.summary}"</p>}
          <ul className="space-y-0.5">
            {(ai.reasons || []).map((x, i) => <li key={i} className="text-white/65 text-xs">• {x}</li>)}
          </ul>
          {ai.missing_items?.length > 0 && (
            <div className="mt-2 text-xs"><span className="text-orange-300 font-semibold">Waiting on customer: </span>
              <span className="text-white/65">{ai.missing_items.join("; ")}</span>
              <span className="text-white/35"> — automated reminders running; auto-expires if never received.</span></div>
          )}
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid={`ai-explain-btn-${r.id}`} onClick={() => ask("explain")} disabled={!!asking}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/5 border border-white/15 text-white/75 hover:text-white text-xs font-semibold disabled:opacity-50">
              {asking === "explain" ? <Loader2 size={12} className="animate-spin" /> : <Brain size={12} />} Explain Recommendation
            </button>
            <button data-testid={`ai-wwyd-btn-${r.id}`} onClick={() => ask("owner_take")} disabled={!!asking}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F2B705]/10 border border-[#F2B705]/30 text-[#F2B705] text-xs font-semibold disabled:opacity-50">
              {asking === "owner_take" ? <Loader2 size={12} className="animate-spin" /> : <Lightbulb size={12} />} What Would You Do?
            </button>
          </div>
          {answers.explain && (
            <div data-testid={`ai-explain-text-${r.id}`} className="mt-2 bg-black/50 border border-white/10 rounded-md p-2.5 text-white/70 text-xs whitespace-pre-line">{answers.explain}</div>
          )}
          {answers.owner_take && (
            <div data-testid={`ai-wwyd-text-${r.id}`} className="mt-2 bg-[#F2B705]/5 border border-[#F2B705]/25 rounded-md p-2.5 text-white/75 text-xs whitespace-pre-line">
              <span className="text-[#F2B705] font-bold">"If I owned AutoHire, I would…" </span><br />{answers.owner_take}</div>
          )}
          <div className="text-white/30 text-[10px] mt-2">Internal recommendation only — management makes every approval decision. Generated {fmtDateTime(ai.generated_at)}</div>
        </>
      )}
    </div>
  );
}

const DOC_META = [
  ["license_front", "license_front_path", "Driver's License (Front)"],
  ["license_back", "license_back_path", "Driver's License (Back)"],
  ["insurance_card", "insurance_card_path", "Insurance Card"],
  ["selfie", "selfie_path", "Selfie Verification"],
  ["additional_driver", "additional_driver_license_path", "Additional Driver's License"],
];

function DocThumb({ path, token }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!path) return;
    let live = true, obj;
    axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((res) => { if (live) { obj = URL.createObjectURL(res.data); setUrl(obj); } }).catch(() => {});
    return () => { live = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path, token]);
  return (
    <a href={url || undefined} target="_blank" rel="noreferrer" className="h-12 w-16 rounded border border-white/15 overflow-hidden bg-black flex items-center justify-center shrink-0">
      {url ? <img src={url} alt="doc" className="w-full h-full object-cover" /> : <Loader2 size={11} className="animate-spin text-white/30" />}
    </a>
  );
}

function DocCenter({ r, H, token, reload }) {
  const [busy, setBusy] = useState("");
  const ds = r.doc_status || { required: [], uploaded: {}, missing: [] };
  const dv = r.doc_verifications || {};
  const pickup = (r.pickup_date || "").slice(0, 10);
  const verify = async (doc, verified) => {
    setBusy(doc);
    try { await axios.post(`${API}/reservations/${r.id}/verify-doc`, { doc, verified }, H); reload(); }
    catch { toast.error("Could not update verification."); }
    finally { setBusy(""); }
  };
  const aiInsurance = async () => {
    setBusy("ai-ins");
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/verify-insurance`, {}, H);
      const e = data.verification.extracted;
      toast.success(`AI read: ${e.insurer || "?"} — expires ${e.expiration_date || "?"}, name match ${data.verification.name_match ? "✓" : "✗"}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "AI verification failed."); }
    finally { setBusy(""); }
  };
  const requestDocs = async () => {
    setBusy("req");
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/request-docs`, { docs: [] }, H);
      toast.success(`Secure upload link sent for: ${data.docs.join(", ")}`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request."); }
    finally { setBusy(""); }
  };
  return (
    <div data-testid={`doc-center-${r.id}`} className="bg-black/40 border border-white/10 rounded-md p-3">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <FileCheck size={14} className="text-[#F2B705]" />
        <span className="text-white font-semibold text-sm">Document Verification Center</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${ds.level === "green" ? "bg-green-500/15 text-green-400" : ds.level === "yellow" ? "bg-[#F2B705]/15 text-[#F2B705]" : "bg-red-500/15 text-red-400"}`}>
          {ds.level === "green" ? "🟢 Complete" : ds.level === "yellow" ? "🟡 Waiting on Customer" : "🔴 Missing"}
        </span>
        {ds.missing.length > 0 && (
          <button data-testid={`request-docs-btn-${r.id}`} onClick={requestDocs} disabled={!!busy}
            className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#F2B705]/10 border border-[#F2B705]/30 text-[#F2B705] text-xs font-semibold disabled:opacity-50">
            {busy === "req" ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Request Updated Documents
          </button>
        )}
      </div>
      <div className="space-y-2">
        {DOC_META.filter(([key, field]) => ds.required.includes(key) || r[field]).map(([key, field, label]) => {
          const path = r[field];
          const v = dv[key] || {};
          const superseded = (r.doc_history || []).filter((h) => h.doc === key).length;
          const expiry = key.startsWith("license") ? (r.dl_expiration || "").slice(0, 10)
            : key === "insurance_card" ? (v.extracted?.expiration_date || r.policy_expiration || "").slice(0, 10) : "";
          const expiresBeforePickup = expiry && pickup && expiry < pickup;
          return (
            <div key={key} className="flex items-center gap-3 bg-white/[0.03] rounded-md px-2.5 py-2">
              {path ? <DocThumb path={path} token={token} /> : <div className="h-12 w-16 rounded border border-dashed border-white/15 flex items-center justify-center text-white/25 text-[10px] shrink-0">none</div>}
              <div className="min-w-0 flex-1">
                <div className="text-white text-xs font-semibold">{label}
                  {r.doc_sources?.[key] === "vault" && <span className="ml-1.5 text-[10px] text-blue-300">from Customer Vault</span>}
                  {superseded > 0 && <span className="ml-1.5 text-[10px] text-white/35">{superseded} superseded in history</span>}
                </div>
                <div className="text-[11px] mt-0.5">
                  {!path ? <span className="text-red-300">Missing ⚠</span>
                    : v.verified ? <span className="text-green-400">Verified ✅ by {v.by} · {(v.at || "").slice(0, 10)}</span>
                    : v.verified === false ? <span className="text-red-300">Rejected ✗ by {v.by}</span>
                    : <span className="text-white/45">Uploaded — not yet verified</span>}
                  {expiry && <span className={expiresBeforePickup ? "text-red-300 font-semibold" : "text-white/40"}> · Expires {expiry}{expiresBeforePickup ? " — BEFORE PICKUP ⚠" : ""}</span>}
                </div>
              </div>
              {path && (
                <div className="flex gap-1 shrink-0">
                  {key === "insurance_card" && (
                    <button data-testid={`ai-verify-ins-${r.id}`} onClick={aiInsurance} disabled={!!busy}
                      className="px-2 py-1 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white text-[10px] disabled:opacity-50">
                      {busy === "ai-ins" ? "Reading…" : "AI Verify"}
                    </button>
                  )}
                  <button data-testid={`verify-${key}-${r.id}`} onClick={() => verify(key, !(v.verified === true))} disabled={busy === key}
                    className={`px-2 py-1 rounded border text-[10px] disabled:opacity-50 ${v.verified ? "bg-green-500/10 border-green-500/30 text-green-400" : "bg-white/5 border-white/10 text-white/60 hover:text-white"}`}>
                    {v.verified ? "Verified ✓" : "Mark Verified"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {(r.extra_docs || []).length > 0 && (
        <div className="mt-3">
          <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Other Documents</div>
          <div className="flex flex-wrap gap-3">
            {r.extra_docs.map((d, i) => <DocFile key={i} path={d.path} label={d.label} token={token} sub={`by ${d.uploaded_by || "admin"} · ${(d.at || "").slice(0, 10)}`} />)}
          </div>
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-white/5">
        <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">Upload a document for this customer (management)</div>
        <AdminDocUpload entityType="reservation" entityId={r.id} token={token} onUploaded={reload} />
      </div>
    </div>
  );
}


const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const RISK_STYLE = { LOW: "bg-green-500/15 text-green-400 border-green-500/30", MEDIUM: "bg-[#F2B705]/15 text-[#F2B705] border-[#F2B705]/30", HIGH: "bg-red-500/15 text-red-400 border-red-500/30" };
const RISK_EMOJI = { LOW: "🟢", MEDIUM: "🟡", HIGH: "🔴" };

function InsightCards({ r, ins }) {
  if (!ins) return <div className="flex items-center gap-2 text-white/40 text-xs py-2"><Loader2 size={12} className="animate-spin" /> Crunching the numbers…</div>;
  const { money: m, risk, vehicle: v, customer: c, profit: p } = ins;
  const Card = ({ title, testid, children }) => (
    <div data-testid={testid} className="bg-black/40 border border-white/10 rounded-md p-3 min-w-0">
      <div className="text-white/40 text-[10px] uppercase tracking-wider mb-1.5">{title}</div>{children}
    </div>
  );
  const Row = ({ l, val, cls = "text-white/80" }) => (
    <div className="flex justify-between gap-2 text-xs"><span className="text-white/50">{l}</span><span className={`font-semibold ${cls}`}>{val}</span></div>
  );
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
      <Card title="💰 Money Meter — Customer Quote" testid={`money-meter-${r.id}`}>
        {(m.lines || []).length > 0 ? (
          <>
            {m.lines.map((ln, i) => <Row key={i} l={ln.label} val={`$${Number(ln.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />)}
            <div className="border-t border-white/10 mt-1.5 pt-1.5">
              <Row l="Total Quote" val={`$${Number(m.total_opportunity).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} cls="text-[#F2B705] text-sm" />
            </div>
            <div className="text-white/40 text-[10px] mt-1" data-testid={`money-days-${r.id}`}>
              {m.days} rental day{m.days !== 1 ? "s" : ""}{m.pickup_date ? ` · ${m.pickup_date} → ${m.return_date}` : ""} — billed by calendar date from pickup to return
            </div>
          </>
        ) : (
          <>
            <Row l={`Rental (${m.days}d × ${money(m.day_rate)})`} val={money(m.rental_total)} />
            <div className="border-t border-white/10 mt-1.5 pt-1.5">
              <Row l="Total Opportunity" val={money(m.total_opportunity)} cls="text-[#F2B705] text-sm" />
            </div>
          </>
        )}
        {m.estimated && <div className="text-white/30 text-[10px] mt-1">Fleet-average estimate — no vehicle picked</div>}
        <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-bold ${RISK_STYLE[risk]}`} data-testid={`risk-meter-${r.id}`}>
          {RISK_EMOJI[risk]} {risk} RISK
        </div>
      </Card>
      <Card title="⭐ Customer History" testid={`customer-card-${r.id}`}>
        {c.returning ? (
          <>
            <Row l="Rentals" val={c.rentals} />
            <Row l="Late Returns" val={c.late_returns} cls={c.late_returns ? "text-red-400" : "text-green-400"} />
            <Row l="Damage Claims" val={c.damage_claims} cls={c.damage_claims ? "text-red-400" : "text-green-400"} />
            <Row l="Recoveries" val={c.recoveries} cls={c.recoveries ? "text-red-400" : "text-green-400"} />
            {c.reviews_count > 0 && <Row l="Reviews" val={`${"⭐".repeat(Math.round(c.reviews_avg))} (${c.reviews_avg})`} />}
            <div className="border-t border-white/10 mt-1.5 pt-1.5">
              <Row l="Lifetime Revenue" val={money(c.lifetime_revenue)} cls="text-[#F2B705]" />
            </div>
          </>
        ) : <div className="text-white/45 text-xs pt-1">First-time customer — no rental history with AutoHire.</div>}
      </Card>
      <Card title="🚗 Vehicle" testid={`vehicle-card-${r.id}`}>
        {v ? (
          <>
            <div className="text-white text-xs font-semibold mb-1">{v.name}</div>
            <Row l="These dates" val={v.available ? "Available ✅" : "CONFLICT ⛔"} cls={v.available ? "text-green-400" : "text-red-400"} />
            {!v.available && <div className="text-red-300 text-[10px]">{v.conflict}</div>}
            {v.next_service_miles != null && <Row l="Next Service" val={`${v.next_service_miles.toLocaleString()} mi`} cls={v.next_service_miles < 500 ? "text-orange-300" : "text-white/80"} />}
            <Row l="This Month" val={money(v.month_revenue)} />
            <Row l="After Approval" val={money(v.projected_month_after)} cls="text-green-400" />
            <Row l="Lifetime" val={money(v.lifetime_revenue)} />
          </>
        ) : <div className="text-white/45 text-xs pt-1">No specific vehicle requested.</div>}
      </Card>
      <Card title="📈 Profit Predictor (Estimated)" testid={`profit-card-${r.id}`}>
        <div className="text-red-300/80 text-[10px] mb-1.5 font-semibold">🔒 Internal only — never quote these numbers to a customer</div>
        <Row l="Revenue" val={`+${money(p.gross)}`} cls="text-green-400" />
        {p.insurance > 0 && <Row l={`All-In Ins. (${m.days}d × $${p.insurance_per_day})`} val={`−$${Math.round(p.insurance)}`} cls="text-red-300" />}
        <Row l="Fuel est." val={`−${money(p.fuel)}`} cls="text-red-300" />
        <Row l="Cleaning est." val={`−${money(p.cleaning)}`} cls="text-red-300" />
        <Row l="Depreciation est." val={`−${money(p.depreciation)}`} cls="text-red-300" />
        <div className="border-t border-white/10 mt-1.5 pt-1.5">
          <Row l="Projected Profit" val={money(p.projected_profit)} cls="text-[#F2B705] text-sm" />
        </div>
        <div className="text-white/30 text-[10px] mt-1">Editable assumptions — real Financial Center averages replace these as data accrues</div>
      </Card>
    </div>
  );
}

const EDIT_FIELDS = [
  ["name", "Full Name"], ["phone", "Phone"], ["email", "Email"],
  ["date_of_birth", "Date of Birth", "date"], ["dl_state", "DL State"], ["dl_expiration", "DL Expiration", "date"],
  ["address", "Address"], ["city", "City"], ["state", "State"], ["zip_code", "ZIP"],
  ["vehicle", "Vehicle", "vehicle"], ["pickup_time", "Pickup Time"], ["return_time", "Return Time"],
  ["rental_purpose", "Rental Purpose"], ["preferred_contact", "Preferred Contact"], ["heard_about", "Heard About Us"],
  ["drive_where", "Driving Where"], ["estimated_miles", "Est. Miles"], ["overnight_parking", "Overnight Parking"],
  ["additional_driver_name", "Additional Driver"], ["insurance_company", "Insurance Company"], ["policy_expiration", "Policy Expiration", "date"],
];

function EditDetails({ r, H, reload, onMoney }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({});
  const [vehList, setVehList] = useState([]);

  const openEditor = () => {
    setF(Object.fromEntries(EDIT_FIELDS.map(([k]) => [k, r[k] || ""])));
    setOpen(true);
    axios.get(`${API}/reservations/${r.id}/vehicle-options`, H)
      .then(({ data }) => setVehList((data.options || []).map((o) => o.name))).catch(() => {});
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/reservations/${r.id}/details`, { fields: f }, H);
      if (data.changed.length) {
        toast.success(`${data.changed.length} field${data.changed.length === 1 ? "" : "s"} updated — logged to the request history.`);
        if (data.money) onMoney(data.money);
        reload();
      } else toast.info("No changes to save.");
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save changes."); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button data-testid={`edit-details-btn-${r.id}`} onClick={openEditor}
      className="text-[11px] font-bold border border-white/20 text-white/60 px-2.5 py-1 rounded hover:border-[#F2B705] hover:text-[#F2B705]">
      ✎ Edit Details
    </button>
  );
  return (
    <div data-testid={`edit-details-panel-${r.id}`} className="w-full bg-black/40 border border-white/15 rounded-md p-3 space-y-2.5">
      <p className="text-[10px] uppercase tracking-wider text-white/45">Edit Request Details — every change is logged</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {EDIT_FIELDS.map(([k, label, type]) => (
          <label key={k} className="text-[10px] text-white/45">
            {label}
            {type === "vehicle" ? (
              <>
                <input data-testid={`edit-field-${k}`} list={`veh-list-${r.id}`} value={f[k] || ""} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-white/15 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#F2B705]/60" />
                <datalist id={`veh-list-${r.id}`}>{vehList.map((v) => <option key={v} value={v} />)}</datalist>
              </>
            ) : (
              <input data-testid={`edit-field-${k}`} type={type || "text"} value={f[k] || ""} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
                className={`w-full bg-[#0A0A0A] border border-white/15 rounded px-2 py-1.5 text-xs text-white focus:outline-none focus:border-[#F2B705]/60 ${type === "date" ? "[color-scheme:dark]" : ""}`} />
            )}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <button data-testid={`edit-details-save-${r.id}`} onClick={save} disabled={busy}
          className="text-xs font-bold bg-[#F2B705] text-black px-3 py-1.5 rounded hover:bg-white disabled:opacity-50">{busy ? "Saving…" : "Save Changes"}</button>
        <button onClick={() => setOpen(false)} className="text-xs font-bold border border-white/20 text-white/60 px-3 py-1.5 rounded hover:border-white/40">Cancel</button>
      </div>
    </div>
  );
}

function QuoteBuilder({ r, H, money, onMoney }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [custom, setCustom] = useState({ label: "", amount: "" });
  const [delivery, setDelivery] = useState("");
  const [dates, setDates] = useState({ pickup: "", ret: "" });
  useEffect(() => {
    if (money) setDates({ pickup: money.pickup_date || "", ret: money.return_date || "" });
  }, [money?.pickup_date, money?.return_date]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!money) return null;
  const qa = money.addons || {};
  const rates = money.rates || {};
  const insOpt = money.insurance_option || r.insurance_option || "I Am Not Sure";
  const isByoi = money.insurance_required_before_pickup;

  const patchIns = async (val) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/reservations/${r.id}/insurance`, { insurance_option: val }, H);
      onMoney(data.money);
      toast.success(`Insurance set to ${val} — quote updated.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change insurance."); }
    finally { setBusy(false); }
  };
  const patchAddons = async (body, okMsg) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/reservations/${r.id}/quote-addons`, body, H);
      onMoney(data.money);
      if (okMsg) toast.success(okMsg);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update quote."); }
    finally { setBusy(false); }
  };
  const sendInsLink = async () => {
    try {
      await axios.post(`${API}/reservations/${r.id}/request-docs`, { docs: ["insurance_card"], note: "" }, H);
      toast.success("Secure insurance upload link sent by SMS + email.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send link."); }
  };

  const textQuote = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/text-quote`, {}, H);
      onMoney(data.money);
      toast.success(`Quote texted — total $${Number(data.total).toFixed(2)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not text the quote."); }
    finally { setBusy(false); }
  };
  const applyDates = async () => {
    setBusy(true);
    try {
      const { data } = await axios.patch(`${API}/reservations/${r.id}/dates`, { pickup_date: dates.pickup, return_date: dates.ret }, H);
      onMoney(data.money);
      toast.success(`Dates updated — ${data.days} rental day${data.days === 1 ? "" : "s"}, quote recalculated.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change dates."); }
    finally { setBusy(false); }
  };
  const qs = money.quote_sent;
  const quoteCurrent = qs && qs.fingerprint === money.fingerprint;

  const Toggle = ({ on, label, price, onClick, testid }) => (
    <button data-testid={testid} disabled={busy} onClick={onClick}
      className={`flex items-center justify-between gap-2 w-full text-left text-xs rounded-md border px-3 py-2 transition-colors ${on ? "border-[#F2B705] bg-[#F2B705]/[0.07] text-white" : "border-white/12 text-white/60 hover:border-white/25"} disabled:opacity-50`}>
      <span>{on ? "✓ " : "+ "}{label}</span><span className={on ? "text-[#F2B705] font-bold" : "text-white/40"}>{price}</span>
    </button>
  );

  return (
    <div data-testid={`quote-builder-${r.id}`} className="bg-black/40 border border-[#F2B705]/25 rounded-md p-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-white/40 text-[10px] uppercase tracking-wider">Live Quote Builder</span>
        <select data-testid={`res-ins-select-${r.id}`} disabled={busy} value={insOpt}
          onChange={(e) => patchIns(e.target.value)}
          className="bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs text-white focus:outline-none focus:border-[#F2B705]/60">
          {["I Am Not Sure", "Bring Your Own Insurance", "All-In Coverage"].map((o) => <option key={o} value={o}>{o}{o === "All-In Coverage" ? ` (+$${rates.allin_coverage_day || ""}/day)` : ""}</option>)}
        </select>
        {isByoi && (
          <>
            <span className="text-[10px] font-bold text-amber-300 border border-amber-400/40 rounded px-1.5 py-0.5">INSURANCE VERIFICATION REQUIRED BEFORE PICKUP</span>
            <button data-testid={`res-ins-doclink-${r.id}`} onClick={sendInsLink} className="text-[10px] font-bold border border-white/20 text-white/60 px-2 py-0.5 rounded hover:border-[#F2B705] hover:text-[#F2B705]">Send Insurance Upload Link</button>
          </>
        )}
        {insOpt === "I Am Not Sure" && <span className="text-[10px] text-white/40">Insurance undecided — you can change it any time.</span>}
        <button data-testid={`edit-quote-btn-${r.id}`} onClick={() => setOpen((v) => !v)}
          className="ml-auto text-[11px] font-bold border border-[#F2B705]/50 text-[#F2B705] px-2.5 py-1 rounded hover:bg-[#F2B705] hover:text-black">
          {open ? "Close" : "Edit Quote / Add-Ons"}
        </button>
        <button data-testid={`text-quote-btn-${r.id}`} onClick={textQuote} disabled={busy || !r.phone}
          className="text-[11px] font-bold bg-[#F2B705] text-black px-2.5 py-1 rounded hover:bg-white disabled:opacity-50">
          {busy ? "…" : "📲 Text Quote"}
        </button>
      </div>
      {qs && (
        <div data-testid={`quote-sent-status-${r.id}`} className="flex flex-wrap items-center gap-2 text-[10px]">
          {quoteCurrent ? (
            <span className="font-bold text-emerald-300 border border-emerald-500/40 bg-emerald-500/10 rounded px-1.5 py-0.5">Quote Sent ✓ {fmtDateTime(qs.at)} — ${Number(qs.total).toFixed(2)}</span>
          ) : (
            <>
              <span className="font-bold text-amber-300 border border-amber-400/50 bg-amber-400/10 rounded px-1.5 py-0.5">⚠ PRIOR QUOTE OUTDATED — sent {fmtDateTime(qs.at)} at ${Number(qs.total).toFixed(2)}, quote has changed</span>
              <button data-testid={`resend-quote-btn-${r.id}`} onClick={textQuote} disabled={busy} className="font-bold text-[#F2B705] underline hover:text-white disabled:opacity-50">Send updated quote</button>
            </>
          )}
        </div>
      )}
      {open && (
        <div className="grid sm:grid-cols-2 gap-2">
          <div className="sm:col-span-2 flex flex-wrap items-center gap-1.5">
            <span className="text-[10px] text-white/45 uppercase tracking-wider">Rental dates</span>
            <input data-testid={`quote-pickup-date-${r.id}`} type="date" value={dates.pickup} onChange={(e) => setDates((s) => ({ ...s, pickup: e.target.value }))}
              className="bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs [color-scheme:dark] focus:outline-none focus:border-[#F2B705]/60" />
            <span className="text-white/40 text-xs">→</span>
            <input data-testid={`quote-return-date-${r.id}`} type="date" value={dates.ret} onChange={(e) => setDates((s) => ({ ...s, ret: e.target.value }))}
              className="bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs [color-scheme:dark] focus:outline-none focus:border-[#F2B705]/60" />
            <button data-testid={`quote-dates-apply-${r.id}`} disabled={busy || !dates.pickup || !dates.ret || (dates.pickup === money.pickup_date && dates.ret === money.return_date)}
              onClick={applyDates}
              className="text-[10px] font-bold bg-[#F2B705] text-black px-2.5 py-1.5 rounded hover:bg-white disabled:opacity-40">Apply Dates</button>
            <span className="text-[10px] text-white/40">{money.days} rental day{money.days === 1 ? "" : "s"} — days &amp; math recalc instantly</span>
          </div>
          <Toggle testid={`addon-waiver-${r.id}`} on={qa.damage_waiver} label="Damage Waiver (optional — not insurance)"
            price={`$${rates.damage_waiver_day || 15}/day × ${money.days}d`}
            onClick={() => patchAddons({ damage_waiver: !qa.damage_waiver }, qa.damage_waiver ? "Damage Waiver removed." : "Damage Waiver added.")} />
          <Toggle testid={`addon-driver-${r.id}`} on={qa.additional_driver} label="Additional Driver"
            price={`$${rates.additional_driver_day || 15}/day × ${money.days}d`}
            onClick={() => patchAddons({ additional_driver: !qa.additional_driver }, qa.additional_driver ? "Additional Driver removed." : "Additional Driver added.")} />
          <div className="flex items-center gap-1.5">
            <input data-testid={`addon-delivery-${r.id}`} type="number" min="0" placeholder={qa.delivery_fee ? `Delivery: $${qa.delivery_fee}` : "Delivery/Pickup fee $"}
              value={delivery} onChange={(e) => setDelivery(e.target.value)}
              className="flex-1 bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#F2B705]/60" />
            <button data-testid={`addon-delivery-apply-${r.id}`} disabled={busy || delivery === ""} onClick={() => { patchAddons({ delivery_fee: Number(delivery) || 0 }, "Delivery fee updated."); setDelivery(""); }}
              className="text-[10px] font-bold border border-white/20 text-white/60 px-2 py-1.5 rounded hover:border-[#F2B705] hover:text-[#F2B705] disabled:opacity-40">Apply</button>
            {Number(qa.delivery_fee) > 0 && <button data-testid={`addon-delivery-remove-${r.id}`} onClick={() => patchAddons({ delivery_fee: 0 }, "Delivery fee removed.")} className="text-[10px] font-bold border border-red-400/40 text-red-300 px-2 py-1.5 rounded hover:bg-red-500 hover:text-white">✕</button>}
          </div>
          <div className="flex items-center gap-1.5">
            <input data-testid={`addon-custom-label-${r.id}`} placeholder="Custom charge label" value={custom.label} onChange={(e) => setCustom((s) => ({ ...s, label: e.target.value }))}
              className="flex-1 bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#F2B705]/60" />
            <input data-testid={`addon-custom-amount-${r.id}`} type="number" placeholder="$" value={custom.amount} onChange={(e) => setCustom((s) => ({ ...s, amount: e.target.value }))}
              className="w-20 bg-[#0A0A0A] border border-white/15 rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#F2B705]/60" />
            <button data-testid={`addon-custom-add-${r.id}`} disabled={busy || !custom.label.trim() || custom.amount === ""} onClick={() => { patchAddons({ add_custom_label: custom.label, add_custom_amount: Number(custom.amount) || 0 }, "Custom add-on added."); setCustom({ label: "", amount: "" }); }}
              className="text-[10px] font-bold bg-[#F2B705] text-black px-2.5 py-1.5 rounded hover:bg-white disabled:opacity-40">Add</button>
          </div>
          {(qa.custom || []).length > 0 && (
            <div className="sm:col-span-2 flex flex-wrap gap-1.5">
              {qa.custom.map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 text-[10px] border border-white/15 rounded-full px-2 py-1 text-white/70">
                  {c.label} · ${Number(c.amount).toFixed(2)}
                  <button data-testid={`addon-custom-remove-${c.id}`} onClick={() => patchAddons({ remove_custom_id: c.id }, "Custom add-on removed.")} className="text-red-300 hover:text-red-400 font-bold">✕</button>
                </span>
              ))}
            </div>
          )}
          <p className="sm:col-span-2 text-white/30 text-[10px]">Optional products are never auto-added. Prices come from Finance → Pricing Settings. Every change is logged and carries into the booking on Approve &amp; Book.</p>
        </div>
      )}
    </div>
  );
}

function WaitlistAction({ r, H, reload, scope = "row" }) {
  const [busy, setBusy] = useState(false);
  const onWaitlist = r.status === "Waiting List" || r.status === "Waiting List – No Vehicle Available";
  const wr = r.waitlist_enrollment || {};
  const run = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/add-to-waitlist`, {}, H);
      toast.success(data.message_status === "sent" ? "Waitlist confirmation text sent." :
        data.message_status === "skipped" ? "On waitlist — confirmation text skipped (opted out / no phone on file)." :
        data.message_status === "already_enrolled" || data.status === "already_enrolled" ? "Already on the waitlist." :
        "On waitlist — confirmation text failed, you can retry.");
      reload();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Could not add to waitlist."); }
    finally { setBusy(false); }
  };
  if (onWaitlist) {
    const failed = wr.message_status === "failed";
    return (
      <button data-testid={`res-waitlist-badge-${scope}-${r.id}`} disabled={!failed || busy} onClick={failed ? run : (e) => e.stopPropagation()}
        title={failed ? "Confirmation text failed to send — click to retry" : "Already on the waitlist"}
        className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-bold border transition-colors ${failed ? "border-red-500/50 text-red-300 hover:bg-red-500 hover:text-white cursor-pointer" : "border-amber-400/40 text-amber-300 bg-amber-400/10 cursor-default"}`}>
        <Car size={10} /> {busy ? "…" : failed ? "On Waitlist — Retry Text" : "On Waitlist"}
      </button>
    );
  }
  if (["Converted to Booking", "Withdrawn", "Opted Out"].includes(r.status)) return null;
  return (
    <button data-testid={`res-waitlist-add-${scope}-${r.id}`} disabled={busy} onClick={run}
      title="Add this request to the waitlist and text them a confirmation"
      className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-sky-500/15 text-sky-300 text-[10px] font-bold hover:bg-sky-500 hover:text-white transition-colors disabled:opacity-50">
      {busy ? <Loader2 size={10} className="animate-spin" /> : <Car size={10} />} Add to Waitlist
    </button>
  );
}

function Detail({ r, H, token, reload }) {
  const [screen, setScreen] = useState({ call_date: "", call_time: "", called_by: "", impression: "", call_notes: "",
    follow_up_needed: false, follow_up_date: "", screening_decision: "",
    ...Object.fromEntries(SCREEN_CHECKS.map(([k]) => [k, false])), ...(r.screening || {}) });
  const [notes, setNotes] = useState(r.admin_notes || "");
  const [assigned, setAssigned] = useState(r.assigned_admin || "");
  const [conds, setConds] = useState([]);
  const [expires, setExpires] = useState(48);
  const [declineInternal, setDeclineInternal] = useState("");
  const [declineMsg, setDeclineMsg] = useState("Thank you for your interest in AutoHire. We are unable to approve this reservation request at this time. Submission of a request does not guarantee rental approval or vehicle availability.");
  const [showApprove, setShowApprove] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [showScreen, setShowScreen] = useState(false);
  const [ins, setIns] = useState(null);
  const [booking, setBooking] = useState(false);
  const [vehOpts, setVehOpts] = useState(null);
  const [selVehicle, setSelVehicle] = useState("");

  useEffect(() => {
    if (!showApprove || vehOpts) return;
    axios.get(`${API}/reservations/${r.id}/vehicle-options`, H).then(({ data }) => {
      setVehOpts(data.options);
      const req = data.options.find((o) => o.requested);
      setSelVehicle(req?.available ? req.name : (data.options.find((o) => o.available)?.name || req?.name || ""));
    }).catch(() => setVehOpts([]));
    // eslint-disable-next-line
  }, [showApprove]);

  useEffect(() => {
    axios.get(`${API}/reservations/${r.id}/insights`, H).then((x) => setIns(x.data)).catch(() => {});
    // eslint-disable-next-line
  }, [r.id]);

  const selOpt = vehOpts?.find((o) => o.name === selVehicle);
  const needsOverride = !!(selOpt && !selOpt.available);

  const approveBook = async () => {
    if (needsOverride && !window.confirm(
      `OVERRIDE WARNING\n\n${selVehicle} shows BOOKED for ${r.pickup_date} → ${r.return_date}${selOpt?.conflict ? ` (${selOpt.conflict})` : ""}.\n\nBooking anyway will DOUBLE-BOOK this car. Only do this if you know the other rental ends early or the calendar is wrong.\n\nOverride and book anyway?`)) return;
    setBooking(true);
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/approve-book`, { conditions: conds, vehicle: selVehicle, override: needsOverride }, H);
      toast.success(`Booked! ${data.vehicle} reserved ($${data.total_price}) — ${data.status}. $20 payment request ${data.reservation_payment_sms_sent ? "sent by SMS" : "link created (SMS not sent)"}.`);
      setShowApprove(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve & Book failed."); }
    finally { setBooking(false); }
  };

  const rebuildBooking = async () => {
    if (!window.confirm(`Rebuild ${r.name}'s booking from this reservation? Extensions, invoices and payments will re-attach automatically.`)) return;
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/rebuild-booking`, {}, H);
      toast.success(data.restored
        ? "Booking restored from archive — it's back in Bookings."
        : data.reactivated
          ? "Booking reactivated — it was cancelled/closed. It's back under Active Bookings now."
          : `Booking rebuilt (${data.extensions_reattached} extension(s) re-attached, paid $${(data.amount_paid || 0).toLocaleString()}). Verify totals in Financials.`);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not rebuild the booking."); }
  };

  const patch = async (body, msg) => {
    try { await axios.patch(`${API}/reservations/${r.id}`, body, H); toast.success(msg); reload(); }
    catch { toast.error("Update failed."); }
  };
  const saveScreening = async () => {
    try { await axios.post(`${API}/reservations/${r.id}/screening`, screen, H); toast.success("Screening saved."); reload(); }
    catch { toast.error("Could not save screening."); }
  };
  const approve = async () => {
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/approve`, { conditions: conds, expires_hours: Number(expires) || 48 }, H);
      toast.success(`Approved — private link sent (${data.status}).`); setShowApprove(false); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed."); }
  };
  const markNoShow = async () => {
    if (!window.confirm(`Mark ${r.name} as a NO-CALL / NO-SHOW?\n\nThey will be automatically added to the Do Not Rent list and any other pending requests from them will be declined.`)) return;
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/no-show`, {}, H);
      toast.success(`No-show recorded — ${data.dnr_added} added to the Do Not Rent list.`);
      reload();
    } catch { toast.error("Could not mark no-show."); }
  };

  const decline = async () => {
    if (!declineInternal.trim()) return toast.error("Internal decline reason is required.");
    try { await axios.post(`${API}/reservations/${r.id}/decline`, { internal_reason: declineInternal, customer_message: declineMsg }, H);
      toast.success("Declined — customer notified."); setShowDecline(false); reload();
    } catch { toast.error("Decline failed."); }
  };

  const info = [["DOB / Age", `${r.date_of_birth || "—"} (${r.age ?? "?"}y)`], ["Address", [r.address, r.city, r.state, r.zip_code].filter(Boolean).join(", ")],
    ["Preferred Contact", r.preferred_contact], ["License", `${r.dl_state} · exp ${r.dl_expiration || "—"}`],
    ["Heard About Us", r.heard_about ? r.heard_about + (r.heard_about_other ? ` — ${r.heard_about_other}` : "") : "—"],
    ["Dates", `${r.pickup_date} ${r.pickup_time} → ${r.return_date} ${r.return_time}`], ["Vehicle", r.vehicle || "No preference"],
    ["Purpose", r.rental_purpose + (r.purpose_other ? ` — ${r.purpose_other}` : "")], ["Days / Miles", `${r.rental_days || "?"} days · ~${r.estimated_miles || "?"} mi`],
    ["Driving Where", r.drive_where || "—"], ["Overnight Parking", r.overnight_parking || "—"],
    ["Insurance", r.insurance_option + (r.insurance_company ? ` (${r.insurance_company})` : "")],
    ["Additional Driver", r.other_drivers ? `${r.additional_driver_name || "?"} (${r.additional_driver_age || "?"}y, ${r.additional_driver_dl_state})` : "None"],
    ["History Explanations", r.history_explanations || "—"]];

  return (
    <div className="border-t border-white/10 px-5 py-4 space-y-4 bg-black/30">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
        {info.map(([k, v]) => <div key={k}><span className="text-white/40 text-xs uppercase tracking-wider">{k}: </span><span className="text-white/80">{v}</span></div>)}
      </div>

      {r.matches?.length > 0 && (
        <div className="bg-blue-950/30 border border-blue-500/20 rounded-md p-3 text-xs">
          <div className="text-blue-300 font-semibold mb-1">Possible existing customer matches (internal):</div>
          {r.matches.map((m, i) => <div key={i} className="text-white/60">{m.name} · {m.email} · {m.phone} · {m.vehicle}{m.balance > 0 ? ` · balance $${m.balance}` : ""}</div>)}
        </div>
      )}

      <div className="flex flex-wrap items-start gap-2">
        <EditDetails r={r} H={H} reload={reload} onMoney={(m) => setIns((v) => (v ? { ...v, money: m } : v))} />
      </div>
      <QuoteBuilder r={r} H={H} money={ins?.money} onMoney={(m) => setIns((v) => (v ? { ...v, money: m } : v))} />
      <InsightCards r={r} ins={ins} />

      <DocCenter r={r} H={H} token={token} reload={reload} />

      <AiReviewPanel r={r} H={H} reload={reload} />

      <div className="bg-black/40 border border-white/10 rounded-md p-3">
        <div className={`text-sm font-bold ${LEVEL_COLOR[r.score.level] || "text-white"}`}>Reservation Review Score: {r.score.level}</div>
        <div className="text-white/35 text-[11px] mb-2">{r.score.disclaimer}</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1">
          {r.score.categories.map((c) => (
            <div key={c.category} className="flex items-center gap-1.5 text-xs">
              {c.ok ? <CheckCircle2 size={12} className="text-green-400" /> : <XCircle size={12} className="text-red-400" />}
              <span className="text-white/65">{c.category}</span>{c.note && <span className="text-white/35">({c.note})</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-end">
        <label className="text-xs text-white/50 uppercase tracking-wider">Status
          <select data-testid={`res-status-${r.id}`} value={r.status} onChange={(e) => patch({ status: e.target.value }, `Status → ${e.target.value}`)}
            className="block mt-1 bg-black border border-white/15 rounded-md px-2 py-1.5 text-white text-sm normal-case">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </label>
        <label className="text-xs text-white/50 uppercase tracking-wider">Assigned Admin
          <input value={assigned} onChange={(e) => setAssigned(e.target.value)} onBlur={() => patch({ assigned_admin: assigned }, "Assigned.")}
            className="block mt-1 bg-black border border-white/15 rounded-md px-2 py-1.5 text-white text-sm w-36 normal-case" />
        </label>
        <label className="flex-1 min-w-[220px] text-xs text-white/50 uppercase tracking-wider">Admin Notes
          <input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => patch({ admin_notes: notes }, "Notes saved.")}
            className="block mt-1 w-full bg-black border border-white/15 rounded-md px-2 py-1.5 text-white text-sm normal-case" />
        </label>
        <button data-testid={`res-approve-btn-${r.id}`} onClick={() => setShowApprove(!showApprove)} className="px-4 py-2 rounded-md bg-green-500/90 text-black font-bold text-sm">Approve…</button>
        <button data-testid={`res-decline-btn-${r.id}`} onClick={() => setShowDecline(!showDecline)} className="px-4 py-2 rounded-md bg-red-500/80 text-white font-bold text-sm">Decline…</button>
        {r.status === "Approved" && (
          <button data-testid={`res-noshow-btn-${r.id}`} onClick={markNoShow}
            className="px-4 py-2 rounded-md border-2 border-red-500 text-red-400 font-bold text-sm hover:bg-red-500 hover:text-white transition-colors">No-Show → DNR</button>
        )}
        <WaitlistAction r={r} H={H} reload={reload} scope="detail" />
      </div>

      {showApprove && (
        <div data-testid="approve-panel" className="bg-green-950/20 border border-green-500/30 rounded-md p-4">
          <div className="text-green-400 font-semibold text-sm mb-2">Approve — optional conditions:</div>
          <div className="grid sm:grid-cols-2 gap-1 mb-3">
            {CONDITION_OPTIONS.map((c) => (
              <label key={c} className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                <input type="checkbox" checked={conds.includes(c)} onChange={(e) => setConds(e.target.checked ? [...conds, c] : conds.filter((x) => x !== c))} className="accent-[#F2B705]" />{c}
              </label>
            ))}
          </div>
          <label className="text-xs text-white/50">Link valid for (hours): <input type="number" value={expires} onChange={(e) => setExpires(e.target.value)} className="w-20 bg-black border border-white/15 rounded px-2 py-1 text-white ml-1" /></label>
          <div className="mt-3 mb-1">
            <div className="text-xs text-white/50 mb-1.5">Vehicle for this booking — tap any car to swap {r.vehicle && <span className="text-white/35">(requested: {r.vehicle})</span>}</div>
            {!vehOpts ? <div className="text-white/40 text-xs flex items-center gap-2"><Loader2 size={11} className="animate-spin" /> Checking fleet availability…</div> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5" data-testid={`vehicle-swap-${r.id}`}>
                {vehOpts.map((o) => (
                  <button key={o.name} type="button" data-testid={`veh-opt-${o.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setSelVehicle(o.name)}
                    title={o.conflict || ""}
                    className={`text-left px-2.5 py-2 rounded-md border text-xs transition-colors ${selVehicle === o.name ? (o.available ? "border-[#F2B705] bg-[#F2B705]/10" : "border-red-500 bg-red-500/10") : "border-white/10 bg-black/30"} ${!o.available ? "opacity-70" : ""} hover:border-white/30`}>
                    <div className="font-semibold text-white truncate">{o.name}{o.requested ? " ★" : ""}{o.inactive ? <span className="ml-1 text-[9px] font-bold text-white/40 border border-white/20 rounded px-1 py-px align-middle">HIDDEN FROM SITE</span> : null}</div>
                    <div className="flex justify-between mt-0.5">
                      <span className={o.available ? "text-green-400" : "text-red-300"}>{o.available ? "Available ✅" : "Booked ⛔"}</span>
                      <span className="text-white/50">${Math.round(o.total)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {selVehicle && r.vehicle && vehOpts && !vehOpts.find((o) => o.requested && o.name === selVehicle) && (
              <div className="text-[#F2B705] text-[11px] mt-1.5">⇄ Swapping to {selVehicle} — the customer requested {r.vehicle}. Mention it when you reach out.</div>
            )}
            {needsOverride && (
              <div data-testid="override-warning" className="mt-2 bg-red-950/40 border border-red-500/40 rounded-md px-3 py-2 text-[11px] text-red-300">
                ⚠️ <b>OVERRIDE MODE</b> — {selVehicle} shows Booked{selOpt?.conflict ? ` (${selOpt.conflict})` : ""} for these dates. Booking will <b>double-book</b> this car. The override is logged with your name.
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="approve-book-btn" onClick={approveBook} disabled={booking}
              className={`flex items-center gap-1.5 px-5 py-2 rounded-md font-bold text-sm disabled:opacity-50 ${needsOverride ? "bg-red-500 text-white" : "bg-[#F2B705] text-black"}`}>
              {booking ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />} {needsOverride ? "Override & Book Anyway" : "Approve & Book — Reserve Vehicle Now"}
            </button>
            <button data-testid="approve-confirm-btn" onClick={approve} className="px-5 py-2 rounded-md bg-green-500 text-black font-bold text-sm">Approve &amp; Send Private Booking Link</button>
          </div>
          <div className="text-white/35 text-[10px] mt-1.5">Approve &amp; Book instantly reserves the vehicle, creates the booking, and texts the customer a secure $20 reservation-payment link. The booking is confirmed — and the document/agreement link sent — once that $20 clears. The private-link option lets the customer choose their own vehicle/details instead.</div>
        </div>
      )}

      {r.status === "Converted to Booking" && r.booking_id && (
        <div className="flex items-center justify-between gap-3 bg-blue-950/20 border border-blue-400/25 rounded-md px-4 py-2.5">
          <span className="text-white/55 text-xs">This request was converted to booking <span className="text-blue-300 font-mono">{(r.booking_id || "").slice(0, 8)}</span>. If the booking was accidentally deleted, you can bring it back.</span>
          <button data-testid={`rebuild-booking-${r.id}`} onClick={rebuildBooking}
            className="shrink-0 text-xs font-semibold border border-blue-400/50 text-blue-300 px-3 py-1.5 rounded hover:bg-blue-400 hover:text-black transition-colors">
            Rebuild Booking
          </button>
        </div>
      )}

      {showDecline && (
        <div data-testid="decline-panel" className="bg-red-950/20 border border-red-500/30 rounded-md p-4 space-y-2">
          <label className="block text-xs text-white/50">Internal decline reason (private, required)
            <input data-testid="decline-internal-input" value={declineInternal} onChange={(e) => setDeclineInternal(e.target.value)} className="mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-sm" />
          </label>
          <label className="block text-xs text-white/50">Customer-facing message
            <textarea rows={2} value={declineMsg} onChange={(e) => setDeclineMsg(e.target.value)} className="mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
          </label>
          <button data-testid="decline-confirm-btn" onClick={decline} className="px-5 py-2 rounded-md bg-red-500 text-white font-bold text-sm">Decline & Notify Customer</button>
        </div>
      )}

      <div className="bg-black/40 border border-white/10 rounded-md">
        <button onClick={() => setShowScreen(!showScreen)} className="w-full flex items-center gap-2 px-4 py-2.5 text-left">
          <PhoneCall size={14} className="text-[#F2B705]" /><span className="text-white font-semibold text-sm">Phone Screening</span>
          {r.screening?.call_date && <span className="text-green-400 text-xs">completed {r.screening.call_date}</span>}
          {showScreen ? <ChevronDown size={14} className="ml-auto text-white/40" /> : <ChevronRight size={14} className="ml-auto text-white/40" />}
        </button>
        {showScreen && (
          <div className="border-t border-white/10 p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
              <label className="text-xs text-white/50">Call Date<input type="date" value={screen.call_date} onChange={(e) => setScreen({ ...screen, call_date: e.target.value })} className="block mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs" /></label>
              <label className="text-xs text-white/50">Call Time<input type="time" value={screen.call_time} onChange={(e) => setScreen({ ...screen, call_time: e.target.value })} className="block mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs" /></label>
              <label className="text-xs text-white/50">Called By<input value={screen.called_by} onChange={(e) => setScreen({ ...screen, called_by: e.target.value })} className="block mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs" /></label>
            </div>
            <div className="grid sm:grid-cols-2 gap-1 mb-3">
              {SCREEN_CHECKS.map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                  <input type="checkbox" checked={!!screen[k]} onChange={(e) => setScreen({ ...screen, [k]: e.target.checked })} className="accent-[#F2B705]" />{l}
                </label>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-xs text-white/50">Management Impression
                <select value={screen.impression} onChange={(e) => setScreen({ ...screen, impression: e.target.value })} className="block mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs">
                  <option value="">—</option><option>Good</option><option>Needs Additional Review</option><option>Do Not Approve</option></select>
              </label>
              <label className="text-xs text-white/50">Overall Screening Decision (private)
                <select value={screen.screening_decision} onChange={(e) => setScreen({ ...screen, screening_decision: e.target.value })} className="block mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs">
                  <option value="">—</option><option>Approve</option><option>Approve With Conditions</option><option>Request More Information</option><option>Decline</option></select>
              </label>
            </div>
            <label className="block text-xs text-white/50 mt-3">Call Notes
              <textarea rows={2} value={screen.call_notes} onChange={(e) => setScreen({ ...screen, call_notes: e.target.value })} className="mt-1 w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs" />
            </label>
            <div className="flex items-center gap-3 mt-2">
              <label className="flex items-center gap-1.5 text-xs text-white/60"><input type="checkbox" checked={screen.follow_up_needed} onChange={(e) => setScreen({ ...screen, follow_up_needed: e.target.checked })} className="accent-[#F2B705]" /> Follow-up needed</label>
              {screen.follow_up_needed && <input type="date" value={screen.follow_up_date} onChange={(e) => setScreen({ ...screen, follow_up_date: e.target.value })} className="bg-black border border-white/15 rounded px-2 py-1 text-white text-xs" />}
              <button data-testid="screening-save-btn" onClick={saveScreening} className="ml-auto px-4 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs">Save Screening</button>
            </div>
          </div>
        )}
      </div>

      {(r.booking_token || r.link_sent_at) && (
        <div className="text-xs text-white/50 flex flex-wrap gap-x-4 gap-y-1">
          <span className="flex items-center gap-1"><Link2 size={12} /> Link sent: {fmtDateTime(r.link_sent_at) || "—"}</span>
          <span>Opened: {r.link_opened_at ? fmtDateTime(r.link_opened_at) : "not yet"}</span>
          <span>Expires: {fmtDateTime(r.link_expires_at) || "—"}</span>
          <span>Booking: {r.booking_id ? `✓ ${r.booking_id.slice(0, 8)}…` : "not completed"}</span>
        </div>
      )}

      <div>
        <div className="text-white/50 text-xs uppercase tracking-wider mb-1.5">Communication Log</div>
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {(r.comm_log || []).slice().reverse().map((c, i) => (
            <div key={i} className="text-xs text-white/55"><span className="text-white/30">{fmtDateTime(c.at)}</span> · <span className="text-[#F2B705]/70">{c.kind}</span> — {c.detail}</div>
          ))}
        </div>
      </div>
    </div>
  );
}

const dAge = (iso) => Math.floor((Date.now() - new Date(iso || Date.now()).getTime()) / 86400000);
const QUICK_FILTERS = [
  { key: "7d", label: "7+ Days Old", fn: (r) => dAge(r.created_at) >= 7 },
  { key: "14d", label: "14+ Days Old", fn: (r) => dAge(r.created_at) >= 14 },
  { key: "30d", label: "30+ Days Old", fn: (r) => dAge(r.created_at) >= 30 },
  { key: "no_response", label: "No Response", fn: (r) => ["No Response", "Follow-Up Unanswered"].includes(r.status) },
  { key: "no_reminder", label: "Reminder Not Sent", fn: (r) => !r.last_reminder_at },
  { key: "docs", label: "Documents Pending", fn: (r) => (r.doc_status?.missing || []).length > 0 },
  { key: "waitlist", label: "Waiting List", fn: (r) => (r.status || "").startsWith("Waiting List") },
  { key: "approved_nb", label: "Approved but Not Booked", fn: (r) => ["Approved", "Approved With Conditions"].includes(r.status) },
];
const OPEN_STATUSES = ["New Request", "Contact Needed", "Phone Screening Scheduled", "Under Review", "Documents Requested", "No Response", "Still Interested", "Follow-Up Unanswered", "Waiting List", "Vehicle Offered", "Waiting List – No Vehicle Available"];

const QUICK_ACTION_ITEMS = [
  { key: "sold_out", label: "Currently Sold Out", icon: Ban, desc: "→ Waiting List" },
  { key: "vehicle_available", label: "Vehicle Available", icon: Car, desc: "→ Vehicle Offered" },
  { key: "still_interested", label: "Still Interested?", icon: Hand, desc: "reply YES/NO" },
  { key: "close_request", label: "Close Request", icon: FolderX, desc: "→ Closed" },
];

function QuickActions({ r, H, reload, onCustom }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const act = async (key, label) => {
    setBusy(key);
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/quick-action`, { action: key }, H);
      toast.success(`${label} sent — SMS ${data.sms_sent ? "✓" : "✗"} · Email ${data.email_sent ? "✓" : "✗"}${data.status ? ` · Status → ${data.status}` : ""}`);
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Quick action failed."); }
    finally { setBusy(""); }
  };
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`quick-actions-btn-${r.id}`} onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-500/15 text-emerald-300 text-[10px] font-bold hover:bg-emerald-500 hover:text-black transition-colors">
        <Zap size={10} /> Quick Actions <ChevronDown size={10} />
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-30 bg-[#141414] border border-white/15 rounded-md shadow-xl py-1 w-60 text-left" onMouseLeave={() => setOpen(false)}>
          {QUICK_ACTION_ITEMS.map((it) => (
            <button key={it.key} data-testid={`qa-${it.key}-${r.id}`} onClick={() => act(it.key, it.label)} disabled={!!busy}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/75 hover:bg-white/5 disabled:opacity-50">
              {busy === it.key ? <Loader2 className="animate-spin" size={12} /> : <it.icon size={12} className="text-[#F2B705]" />}
              <span className="flex-1 text-left">{it.label}</span>
              <span className="text-white/30 text-[9px]">{it.desc}</span>
            </button>
          ))}
          <a data-testid={`qa-call-${r.id}`} href={`tel:${r.phone}`}
            className="flex items-center gap-2 px-3 py-2 text-xs text-white/75 hover:bg-white/5">
            <PhoneCall size={12} className="text-[#F2B705]" /> Call Customer
          </a>
          <button data-testid={`qa-custom-${r.id}`} onClick={() => { setOpen(false); onCustom(); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/75 hover:bg-white/5">
            <Mail size={12} className="text-[#F2B705]" /> Custom Message…
          </button>
        </div>
      )}
    </div>
  );
}

function QuickConvertButton({ r, token, reload }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={`quick-convert-btn-${r.id}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Approve & Book without opening this request"
        className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#F2B705]/15 text-[#F2B705] text-[10px] font-bold hover:bg-[#F2B705] hover:text-black transition-colors">
        <Zap size={10} /> Quick Convert
      </button>
      {open && (
        <QuickConvertDialog request={{ id: r.id, name: r.name, vehicle: r.vehicle, pickup_date: r.pickup_date, return_date: r.return_date }}
          token={token} onClose={() => setOpen(false)} onDone={reload} />
      )}
    </>
  );
}

function RowMenu({ r, H, showArchived, reload }) {
  const [open, setOpen] = useState(false);
  const archive = async () => {
    try { await axios.post(`${API}/reservations/${r.id}/archive`, {}, H); toast.success(`${r.name} archived — restore anytime from the Archived view.`); reload(); }
    catch { toast.error("Archive failed."); }
  };
  const restore = async () => {
    try { await axios.post(`${API}/reservations/${r.id}/restore`, {}, H); toast.success(`${r.name} restored.`); reload(); }
    catch { toast.error("Restore failed."); }
  };
  const del = async () => {
    const reason = window.prompt(`PERMANENT DELETE — only for test reservations, spam, duplicates or incorrect entries.\nReservations tied to bookings, payments or agreements can only be archived.\n\nType the reason to confirm deleting ${r.name}'s request forever:`);
    if (!reason) return;
    try { await axios.delete(`${API}/reservations/${r.id}?reason=${encodeURIComponent(reason)}`, H); toast.success("Deleted permanently."); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete blocked."); }
  };
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button data-testid={`res-menu-${r.id}`} onClick={() => setOpen(!open)} className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5"><MoreVertical size={14} /></button>
      {open && (
        <div className="absolute right-0 top-7 z-20 bg-[#141414] border border-white/15 rounded-md shadow-xl py-1 w-44 text-left" onMouseLeave={() => setOpen(false)}>
          {showArchived ? (
            <button data-testid={`res-restore-${r.id}`} onClick={restore} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:bg-white/5"><RotateCcw size={12} /> Restore</button>
          ) : (
            <button data-testid={`res-archive-${r.id}`} onClick={archive} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-white/70 hover:bg-white/5"><Archive size={12} /> Archive</button>
          )}
          <button data-testid={`res-delete-${r.id}`} onClick={del} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-300 hover:bg-red-500/10"><Trash2 size={12} /> Delete Permanently…</button>
        </div>
      )}
    </div>
  );
}

export default function ReservationQueue({ token, focusId, onStatsChanged }) {
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [quick, setQuick] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [reminder, setReminder] = useState(null); // {r} or {bulk:[ids]}
  const [openId, setOpenId] = useState(focusId || null);
  const [showGhlSync, setShowGhlSync] = useState(false);
  useEffect(() => {
    if (focusId && rows) {
      setOpenId(focusId);
      setTimeout(() => document.querySelector(`[data-testid="res-row-${focusId}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
    // eslint-disable-next-line
  }, [focusId, rows === null]);
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    axios.get(`${API}/reservations?${statusFilter ? `status=${encodeURIComponent(statusFilter)}&` : ""}${showArchived ? "archived=true" : ""}`, H).then((r) => setRows(r.data));
    axios.get(`${API}/reservations/stats`, H).then((r) => { setStats(r.data); onStatsChanged?.(r.data); }).catch(() => {});
  };
  useEffect(() => { load(); setSelected(new Set()); /* eslint-disable-next-line */ }, [token, statusFilter, showArchived]);

  if (!rows) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading reservation requests…</div>;

  const qf = QUICK_FILTERS.find((f) => f.key === quick);
  const visible = (qf ? rows.filter(qf.fn) : rows).slice().sort((a, b) => {
    const prio = (x) => (x.high_priority ? -1 : x.status === "Still Interested" ? 0 : 1);
    return prio(a) - prio(b) || (b.created_at || "").localeCompare(a.created_at || "");
  });

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allVisible = visible.length > 0 && visible.every((r) => selected.has(r.id));

  const bulkDo = async (action) => {
    const ids = [...selected];
    if (action === "archive" && !window.confirm(`Archive ${ids.length} reservation(s)? You can restore them anytime.`)) return;
    if (action === "inactive" && !window.confirm(`Mark ${ids.length} reservation(s) as inactive (No Response)?`)) return;
    try {
      const { data } = await axios.post(`${API}/reservations/bulk`, { ids, action }, H);
      toast.success(`${data.ok_count}/${data.total} ${action === "archive" ? "archived" : "marked inactive"}.`);
      setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed."); }
  };

  const dnr = async (r) => {
    const reason = window.prompt(`Add ${r.name} to the DO-NOT-RENT list?\nEvery future reservation request or booking from this email/phone will be blocked automatically.\n\nType the reason to confirm:`);
    if (!reason) return;
    const decline = window.confirm("Also decline this request right now?\n\nOK = add to Do-Not-Rent AND decline this request.\nCancel = add to Do-Not-Rent only (request status stays unchanged).");
    try {
      const { data } = await axios.post(`${API}/reservations/${r.id}/dnr`, { reason, decline }, H);
      toast.success(`${r.name} added to Do-Not-Rent${decline ? (data.auto_declined > 1 ? ` — this + ${data.auto_declined - 1} other matching request(s) declined.` : " — request declined.") : "."}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add to Do-Not-Rent."); }
  };

  const exportCsv = () => {
    const sel = visible.filter((r) => selected.has(r.id));
    const head = ["Name", "Email", "Phone", "Vehicle", "Status", "Created", "Pickup", "Return", "Reminder Sent"];
    const csv = [head.join(","), ...sel.map((r) => [r.name, r.email, r.phone, r.vehicle || "", r.status, (r.created_at || "").slice(0, 10), r.pickup_date, r.return_date, r.last_reminder_at ? (r.last_reminder_at || "").slice(0, 10) : "never"]
      .map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `reservations-export-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Exported ${sel.length} reservation(s).`);
  };

  const alerts = stats?.alerts || {};
  const alertChips = [["new_requests", "New"], ["waiting_over_4h", "Waiting 4h+"], ["out_of_state_review", "Out-of-State"],
    ["screening_needed", "Screening Needed"], ["approved_not_booked", "Approved, Not Booked"], ["link_expiring", "Link Expiring"], ["no_response", "No Response"]];

  return (
    <div data-testid="reservation-queue" className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-display font-bold text-white mr-auto flex items-center gap-2"><ShieldQuestion size={17} className="text-[#F2B705]" /> Reservation Requests{showArchived && <span className="text-white/40 text-sm font-normal">— Archived</span>}</h3>
        {stats && <span className="text-white/40 text-xs">{stats.total} total · {stats.approval_rate}% approved · {stats.converted} converted · ${Number(stats.revenue_from_requests).toLocaleString()} revenue</span>}
        <ManualReservation token={token} onCreated={load} />
        <button data-testid="waitlist-sync-open-btn" onClick={() => setShowGhlSync(!showGhlSync)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border ${showGhlSync ? "bg-sky-500/20 text-sky-300 border-sky-500/40 font-bold" : "border-white/15 text-white/60"}`}>
          <Zap size={12} /> GHL Waitlist Sync
        </button>
        <button data-testid="res-archived-toggle" onClick={() => setShowArchived(!showArchived)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs border ${showArchived ? "bg-[#F2B705] text-black border-[#F2B705] font-bold" : "border-white/15 text-white/60"}`}>
          <Archive size={12} /> {showArchived ? "Back to Active" : "Archived"}
        </button>
        <select data-testid="res-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-black border border-white/15 rounded-md px-3 py-1.5 text-sm text-white/80">
          <option value="">All statuses</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>

      {showGhlSync && <WaitlistSyncPanel token={token} onClose={() => setShowGhlSync(false)} />}

      <div className="flex flex-wrap gap-1.5">
        {QUICK_FILTERS.map((f) => (
          <button key={f.key} data-testid={`res-qf-${f.key}`} onClick={() => setQuick(quick === f.key ? "" : f.key)}
            className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${quick === f.key ? "bg-[#F2B705] text-black border-[#F2B705] font-bold" : "bg-white/5 border-white/10 text-white/55 hover:text-white"}`}>
            {f.label} ({rows.filter(f.fn).length})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {alertChips.filter(([k]) => alerts[k] > 0).map(([k, l]) => (
          <span key={k} className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500/10 border border-red-500/25 text-red-300 text-xs"><Clock size={11} />{l}: {alerts[k]}</span>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 bg-[#141414] border border-[#F2B705]/40 rounded-xl px-4 py-2.5" data-testid="res-bulk-bar">
          <span className="text-white text-sm font-bold">{selected.size} selected</span>
          <button data-testid="bulk-reminder" onClick={() => setReminder({ bulk: [...selected] })} className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#F2B705] text-black font-bold text-xs"><Bell size={11} /> Send Reminder</button>
          <button data-testid="bulk-archive" onClick={() => bulkDo("archive")} className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/15 text-white/70 text-xs"><Archive size={11} /> Archive</button>
          <button data-testid="bulk-inactive" onClick={() => bulkDo("inactive")} className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/15 text-white/70 text-xs">Mark Inactive</button>
          <button data-testid="bulk-export" onClick={exportCsv} className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/15 text-white/70 text-xs"><Download size={11} /> Export CSV</button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-white/40 text-xs hover:text-white">Clear</button>
        </div>
      )}

      <div className="space-y-2.5">
        <label className="flex items-center gap-2 text-xs text-white/45 px-1 cursor-pointer">
          <input type="checkbox" data-testid="res-select-all" checked={allVisible} onChange={() => setSelected(allVisible ? new Set() : new Set(visible.map((r) => r.id)))} className="accent-[#F2B705]" />
          Select all shown ({visible.length})
        </label>
        {visible.map((r) => (
          <div key={r.id} data-testid={`res-row-${r.id}`} className={`bg-[#0C0C0C] border rounded-xl overflow-hidden ${r.status === "Still Interested" ? "border-emerald-500/40" : "border-white/10"}`}>
            <div className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3.5 text-left cursor-pointer" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
              <input type="checkbox" data-testid={`res-check-${r.id}`} checked={selected.has(r.id)} onChange={() => toggleSel(r.id)} onClick={(e) => e.stopPropagation()} className="accent-[#F2B705]" />
              <div className="min-w-[160px]">
                <div className="text-white font-semibold">{r.name} <span className="text-white/40 font-normal text-xs">({r.age ?? "?"}y · {r.dl_state})</span></div>
                <div className="text-white/45 text-xs">{r.phone} · {r.email}</div>
              </div>
              <div className="text-white/60 text-xs">{r.vehicle || "Any vehicle"} · {r.pickup_date} → {r.return_date}</div>
              <div className="text-white/45 text-xs">{r.rental_purpose}</div>
              <div className="flex flex-wrap gap-1 ml-auto items-center">
                {r.last_reminder_at && (
                  <span data-testid={`reminder-badge-${r.id}`} title={`Reminder sent ${fmtDateTime(r.last_reminder_at)}${r.reminder_count > 1 ? ` · ${r.reminder_count} total` : ""}`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-300 text-[10px] font-semibold">
                    <Bell size={9} /> Reminder Sent{r.reminder_count > 1 ? ` ×${r.reminder_count}` : ""}
                  </span>
                )}
                {r.doc_status && (
                  <span data-testid={`doc-chip-${r.id}`} title={r.doc_status.missing_labels?.join(", ") || "All documents on file"}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${r.doc_status.level === "green" ? "bg-green-500/10 text-green-400" : r.doc_status.level === "yellow" ? "bg-[#F2B705]/10 text-[#F2B705]" : "bg-red-500/10 text-red-300"}`}>
                    {r.doc_status.level === "green" ? "Docs ✅" : r.doc_status.level === "yellow" ? "Docs 🟡" : "Docs 🔴"}
                  </span>
                )}
                {r.ai_review && (
                  <span data-testid={`ai-badge-${r.id}`} title={`AI: ${r.ai_review.recommendation}`}
                    className={`px-2 py-0.5 rounded-full bg-white/5 text-[11px] font-semibold ${AI_COLOR[r.ai_review.recommendation] || "text-white/60"}`}>
                    {AI_EMOJI[r.ai_review.recommendation]} {r.ai_review.confidence}%
                  </span>
                )}
                {(r.fraud_flags || []).slice(0, 2).map((f) => (
                  <span key={f.code} data-testid={`fraud-badge-${r.id}-${f.code}`} title={f.detail}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-[10px] font-bold">
                    <ShieldAlert size={9} /> {f.label}
                  </span>
                ))}
                {(r.fraud_flags || []).length > 2 && <span className="text-orange-300/70 text-[10px]">+{r.fraud_flags.length - 2} fraud</span>}
                <RiskBadge phone={r.phone} token={token} />
                {r.flags.slice(0, 2).map((fl) => <span key={fl} className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 text-[10px]"><Flag size={9} />{fl}</span>)}
                {r.flags.length > 2 && <span className="text-white/40 text-[10px]">+{r.flags.length - 2} flags</span>}
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLOR[r.status] || "bg-white/5 text-white/60"}`}>{r.status}</span>
                <span className="text-white/30 text-xs">{fmtDateTime(r.created_at)}</span>
                <WaitlistAction r={r} H={H} reload={load} scope="row" />
                {OPEN_STATUSES.includes(r.status) && !showArchived && (
                  <button data-testid={`res-reminder-btn-${r.id}`} onClick={(e) => { e.stopPropagation(); setReminder({ r }); }}
                    title="Send a follow-up reminder"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#F2B705]/15 text-[#F2B705] text-[10px] font-bold hover:bg-[#F2B705] hover:text-black transition-colors">
                    <Bell size={10} /> Reminder
                  </button>
                )}
                {!showArchived && !["Converted to Booking", "Withdrawn", "Opted Out"].includes(r.status) && (
                  <QuickActions r={r} H={H} reload={load} onCustom={() => setReminder({ r })} />
                )}
                {!showArchived && OPEN_STATUSES.includes(r.status) && r.pickup_date && r.return_date && (
                  <QuickConvertButton r={r} token={token} reload={load} />
                )}
                {!showArchived && !r.dnr_match && (
                  <button data-testid={`res-dnr-btn-${r.id}`} onClick={(e) => { e.stopPropagation(); dnr(r); }}
                    title="Add this person to the Do-Not-Rent list"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-red-500/15 text-red-300 text-[10px] font-bold hover:bg-red-500 hover:text-white transition-colors">
                    <Ban size={10} /> Do Not Rent
                  </button>
                )}
                <RowMenu r={r} H={H} showArchived={showArchived} reload={load} />
                {openId === r.id ? <ChevronDown size={15} className="text-white/40" /> : <ChevronRight size={15} className="text-white/40" />}
              </div>
            </div>
            {openId === r.id && <Detail r={r} H={H} token={token} reload={load} />}
          </div>
        ))}
        {visible.length === 0 && <div className="text-white/40 text-sm py-10 text-center">No reservation requests{showArchived ? " in the archive" : statusFilter || quick ? " match this filter" : " yet — they'll appear here the moment someone submits the form"}.</div>}
      </div>

      {reminder && (
        <ReminderDialog r={reminder.r} bulkIds={reminder.bulk} token={token}
          onClose={() => setReminder(null)} onSent={() => { setSelected(new Set()); load(); }} />
      )}
    </div>
  );
}
